"use client";

import { Copy, MapPin, Navigation, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectChannels() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const left = useRevealOnScroll<HTMLDivElement>();
  const right = useRevealOnScroll<HTMLDivElement>();
  const infoBlocks = [{
    label: "OFFICE LOCATION",
    lines: ["Myles Dacres Studio", "42 Creative Quarter", "London, United Kingdom"],
    note: ""
  }, {
    label: "DIRECT CONTACT",
    lines: ["+44 20 7946 0123", "hello@mylesdacres.com"],
    note: "RESPONSE WITHIN 24 HOURS"
  }, {
    label: "AVAILABILITY",
    lines: ["Mon-Fri: 09:00 - 18:00", "Consultations by appointment"],
    note: "GLOBAL TIMEZONES ACCOMMODATED"
  }, {
    label: "CONNECT",
    lines: ["LinkedIn: /in/mylesdacres", "Twitter: @mylesdacres"],
    note: "STRICTLY PROFESSIONAL INQUIRIES"
  }];
  return <section data-section-id="4854" id="direct-channels" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px w-12 bg-primary" />
            <span className="font-default text-xs tracking-[0.25em] text-primary uppercase">
              Direct Channels
            </span>
          </div>
          <h2 className="font-default font-medium text-4xl md:text-6xl lg:text-[88px] leading-[0.9] tracking-[0.15em] text-foreground">
            Let's talk.
          </h2>
        </div>

        {}
        <div className="mt-16 md:mt-20 lg:mt-[72px] grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div ref={left.ref} className={`lg:col-span-5 transition-all duration-700 delay-100 ${left.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex flex-col">
              {infoBlocks.map((block, i) => <div data-index={i} key={i} className="py-6 first:pt-0">
                  {i > 0 && <div className="relative mb-6 -mt-6">
                      <div className="h-px w-full bg-border" />
                      <Plus className="absolute -top-[7px] left-0 size-3.5 text-border bg-background p-0.5" strokeWidth={1.5} />
                    </div>}
                  <div className="font-default text-[11px] tracking-[0.25em] text-muted-foreground uppercase mb-4">
                    {block.label}
                  </div>
                  <div className="space-y-1">
                    {block.lines.map((line, j) => <p key={j} className="font-default text-lg md:text-xl text-foreground leading-snug">
                        {line}
                      </p>)}
                  </div>
                  {block.note && <p className="mt-4 font-default text-[10px] tracking-[0.2em] text-primary uppercase">
                      {block.note}
                    </p>}
                </div>)}
            </div>
          </div>

          {}
          <div ref={right.ref} className={`lg:col-span-7 transition-all duration-700 delay-200 ${right.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="size-1.5 bg-primary" />
                <span className="font-default text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
                  51.5074°N, 0.1278°W
                </span>
              </div>
              <span className="font-default text-[11px] tracking-[0.2em] text-muted-foreground uppercase hidden md:block">
                STUDIO / 01
              </span>
            </div>

            {}
            <div className="relative border border-border">
              {}
              <div className="absolute -top-1 -left-1 size-3 border-t border-l border-primary z-10" />
              <div className="absolute -top-1 -right-1 size-3 border-t border-r border-primary z-10" />
              <div className="absolute -bottom-1 -left-1 size-3 border-b border-l border-primary z-10" />
              <div className="absolute -bottom-1 -right-1 size-3 border-b border-r border-primary z-10" />

              {}
              <div className="relative aspect-[4/3] md:aspect-[5/4] w-full overflow-hidden bg-muted">
                <iframe title="Myles Dacres Studio Location" src="https://www.google.com/maps?q=51.5074,-0.1278&hl=en&z=14&output=embed" className="w-full h-full grayscale contrast-100" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                {}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 animate-ping bg-primary/40 size-8 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2" />
                    <MapPin className="size-10 text-primary relative" fill="currentColor" strokeWidth={1} />
                  </div>
                </div>

                {}
                <div className="pointer-events-none absolute top-0 left-0 right-0 flex justify-between px-4 py-2">
                  {Array.from({
                  length: 8
                }).map((_, i) => <span data-index={i} key={i} className="block h-2 w-px bg-primary/40" />)}
                </div>
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex justify-between px-4 py-2">
                  {Array.from({
                  length: 8
                }).map((_, i) => <span data-index={i} key={i} className="block h-2 w-px bg-primary/40" />)}
                </div>
              </div>
            </div>

            {}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button asChild variant="outline" className="font-default text-[11px] tracking-[0.25em] uppercase border-primary bg-primary text-white hover:opacity-90 h-12 px-6">
                <Link to="https://www.google.com/maps/dir/?api=1&destination=51.5074,-0.1278" newTab>
                  <Navigation className="size-3.5" />
                  Get Directions
                </Link>
              </Button>
              <Button variant="ghost" className="font-default text-[11px] tracking-[0.25em] uppercase text-foreground border border-border hover:bg-accent h-12 px-6" onClick={() => {
              if (typeof navigator !== "undefined" && navigator.clipboard) {
                navigator.clipboard.writeText("Myles Dacres Studio, 42 Creative Quarter, London, United Kingdom");
              }
            }}>
                <Copy className="size-3.5" />
                Copy Address
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}